import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Alert,
  Container,
  Fade,
  Paper,
  PaperProps,
  Slide,
  Stack,
  Typography,
  alertClasses,
  styled,
  useTheme,
} from "@mui/material";
import { StackProps } from "@mui/material";

import { useSnackbar } from "@/hooks";

interface SnacksProps extends StackProps {}

const StyledSnacks = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  left: 0,
  pointerEvents: "none",
  position: "fixed",
  right: 0,
  top: `calc(${theme.spacing(0.75)} + env(safe-area-inset-top))`,
  zIndex: theme.zIndex.appBar + 1,
  [theme.breakpoints.up("sm")]: {
    top: `calc(${theme.spacing(0.75)} + env(safe-area-inset-top))`,
  },
}));

export const Snacks: React.FC<SnacksProps> = () => {
  const { t } = useTranslation();
  const { snackbars, hide, queuedCount } = useSnackbar();

  const theme = useTheme();

  const snackPaperStyles: PaperProps["sx"] = useMemo(() => {
    return {
      ...theme.typography.caption,
      backdropFilter: "blur(5px)",
      background: theme.alpha(theme.palette.background.paper, 0.8),
      borderColor: theme.alpha(theme.palette.primary.main, 0.5),
      borderRadius: `${(theme.shape.borderRadius as number) * 0.75}px !important`,
      boxShadow: theme.shadows[2],
      color: theme.palette.primary.dark,
      displayPrint: "none",
      fontWeight: 500,
      padding: theme.spacing(0.75, 2),
      pointerEvents: "auto",
      [`& .${alertClasses.icon}`]: {
        color: theme.palette.primary.light,
      },
      [`& .${alertClasses.action}`]: {
        padding: 0,
      },
    };
  }, [theme]);

  return (
    <StyledSnacks spacing={0.5}>
      {snackbars.map((snackbar, index) => (
        <Slide
          direction="down"
          in
          key={snackbar.id}
          style={{ transitionDelay: `${index * 350}ms` }}
        >
          <Container maxWidth="sm">
            <Alert
              onClose={() => hide(snackbar.id)}
              severity={snackbar.severity}
              slotProps={{ message: { sx: { textAlign: "center" } } }}
              sx={{ "&": { ...snackPaperStyles } }}
              variant="outlined"
            >
              {snackbar.text}
            </Alert>
          </Container>
        </Slide>
      ))}
      {queuedCount > 0 && (
        <Fade in key={queuedCount}>
          <Paper
            sx={{ "&": { ...snackPaperStyles, py: 0.25, px: 1 } }}
            variant="outlined"
          >
            <Typography color="primary.dark" fontWeight={500} variant="caption">
              {t("notifications.more", { count: queuedCount })}
            </Typography>
          </Paper>
        </Fade>
      )}
    </StyledSnacks>
  );
};
